// Single entry point for every global/app-level keyboard shortcut in the client.
// We route all of them through TanStack Hotkeys (@tanstack/svelte-hotkeys) instead
// of hand-rolled `<svelte:window onkeydown>` handlers, so behaviour is consistent:
//   · type-safe hotkey strings ("Escape", "Enter", "M", "Mod+S", …)
//   · cross-platform "Mod" (⌘ on macOS, Ctrl elsewhere)
//   · sensible input handling, single-key shortcuts (M, Enter) are ignored while
//     the user is typing in a field; Escape still fires so dialogs can close.
//
// Element-scoped key handlers (form `Enter`-to-submit, a11y `Enter`/`Space` to
// activate a focusable card) and the WorldRenderer's held-key WASD movement loop
// are deliberately NOT routed here, those are local input concerns, not global
// shortcuts.
//
// `createHotkey`/`createHotkeyAttachment` must be called during component init
// (top-level in <script>), like a Svelte hook. The registration is torn down
// automatically when the component unmounts.
export { createHotkey, createHotkeyAttachment } from "@tanstack/svelte-hotkeys";
import { createHotkey } from "@tanstack/svelte-hotkeys";
import type { CreateHotkeyOptions } from "@tanstack/svelte-hotkeys";

type MaybeGetter<T> = T | (() => T);

/**
 * The ubiquitous "Escape closes this overlay" shortcut. Escape fires even when a
 * control inside the overlay is focused (TanStack defaults `ignoreInputs:false`
 * for Escape), which is what we want for dismissing dialogs.
 *
 * @param close   invoked when Escape is pressed
 * @param options optional reactive options getter, e.g. `() => ({ enabled: open })`
 */
export function onEscape(
  close: () => void,
  options?: MaybeGetter<CreateHotkeyOptions>,
): void {
  // Escape is registered by many overlays at once (all on `document`); allow the
  // duplicate registrations to coexist silently, every enabled handler fires,
  // which is exactly the old `<svelte:window onkeydown>` behaviour.
  createHotkey("Escape", () => close(), () => ({
    conflictBehavior: "allow",
    ...(typeof options === "function" ? options() : options),
  }));
}
